import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@material-ui/core';
import {
  InstructionsState,
  useInstructions,
} from 'app/shared/context-hooks/use-instructions.hook';
import styledFuncs from 'app/styles/styledFuncs';
import { useWatchAsRef } from 'app/tools/hooks/misc-hooks';
import { jsxContains } from 'app/tools/utils';
import _ from 'lodash';
import { useSubscription } from 'observable-hooks';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import styled from 'styled-components/macro';
import { Merge } from 'type-fest';
import Button from '../button/Button';
import Icon from '../icon/Icon';
import IconLib from '../icon/icon-lib';

const StyledDialog = styled(Dialog)`
  .MuiDialogTitle-root {
    padding-bottom: ${styledFuncs.pad()};
  }
  .MuiDialogContentText-root:last-child {
    margin-bottom: 0;
  }
`;

const getDialogMsgArray = (
  { message }: BasicDialogOpts = { message: '' }
): (string | JSX.Element)[] =>
  _.isArray(message) ? message : [message];

export type BasicDialogOpts = {
  message: (string | JSX.Element)[] | string | JSX.Element;
  title?: string;
  actions?: {
    text?: string;
    callback?: () => void | boolean;
  }[];
  onDismissed?: () => void;
};
export type InstructionDialogOpts = Merge<
  BasicDialogOpts,
  {
    instructionName: keyof InstructionsState;
    noShowAfterDismiss?: boolean;
    actions?: {
      text?: string;
      callback?: () => void;
      dontShowAgain?: boolean;
    }[];
  }
>;

type Opts = BasicDialogOpts;

type Props = {
  dialog$: Observable<BasicDialogOpts>;
  disableAutoFocus?: boolean;
};
export default function BasicDialog({
  dialog$,
  disableAutoFocus = false,
}: Props): JSX.Element {
  const history = useHistory();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentDialog, setCurrentDialog] = useState<Opts>();
  const [dialogStack, setDialogStack] = useState<Opts[]>([]);

  useSubscription(dialog$, dialog => {
    setDialogStack(s => [...s, { ...dialog }]);
  });

  useEffect(() => {
    if (dialogStack.length && !currentDialog) {
      setCurrentDialog(dialogStack[0]);
      setDialogStack(s => s.slice(1));
      setDialogOpen(true);
    }
  }, [currentDialog, dialogStack]);

  const dialogPresent = dialogStack.length || currentDialog;
  useEffect(() => {
    if (dialogPresent) {
      return history.listen(() => {
        setDialogStack([]);
        setDialogOpen(false);
      });
    }
  }, [dialogPresent, history]);

  return (
    <StyledDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      TransitionProps={{
        onExited: () => {
          currentDialog?.onDismissed?.();
          setCurrentDialog(undefined);
        },
      }}
      disableEnforceFocus={disableAutoFocus}
      {...{ disableAutoFocus }}
    >
      <IconButton
        aria-label="close dialog box"
        onClick={() => setDialogOpen(false)}
        className="absolute top-0.5 right-0.5 p-0.2"
      >
        <Icon icon={IconLib.CROSS} size={20} />
      </IconButton>
      {currentDialog?.title && (
        <DialogTitle>{currentDialog.title}</DialogTitle>
      )}
      <DialogContent>
        {getDialogMsgArray(currentDialog).map((message, i) =>
          jsxContains(message, 'p') ? (
            // NOTE: avoids nesting p tags (illegal html)
            // eslint-disable-next-line react/no-array-index-key
            <div key={i}>{message}</div>
          ) : (
            // eslint-disable-next-line react/no-array-index-key
            <DialogContentText key={i}>{message}</DialogContentText>
          )
        )}
      </DialogContent>
      <DialogActions>
        {currentDialog?.actions?.map((action, i) => (
          <Button
            key={action?.text || i}
            onClick={() => {
              const result = action?.callback?.();
              if (result !== false) setDialogOpen(false);
            }}
            variant="text"
            fullWidth
            color="primary"
          >
            {action?.text || 'Close'}
          </Button>
        ))}
      </DialogActions>
    </StyledDialog>
  );
}

export const useInstructionToBasicDialog = (): ((
  instruction$: Observable<InstructionDialogOpts>
) => Observable<BasicDialogOpts>) => {
  const [instructionsState, updateInstructionState] =
    useInstructions();
  const instructionsRef = useWatchAsRef(instructionsState);

  return instruction$ =>
    instruction$.pipe(
      filter(
        instruction =>
          instructionsRef.current[instruction.instructionName] !==
          false
      ),
      map(
        ({
          actions,
          instructionName,
          noShowAfterDismiss,
          ...instruction
        }) => {
          const hidePermanently = () =>
            updateInstructionState({
              [instructionName]: false,
            });
          return {
            ...instruction,
            onDismissed: noShowAfterDismiss
              ? hidePermanently
              : undefined,
            actions: actions?.map(
              ({ text, callback, dontShowAgain }) => ({
                text,
                callback: () => {
                  callback?.();
                  if (dontShowAgain) hidePermanently();
                },
              })
            ),
          };
        }
      )
    );
};
